import type { RouterOptions } from '@nuxt/schema'
import type { RouteLocationNormalized, RouterScrollBehavior } from 'vue-router'
import { START_LOCATION } from 'vue-router'
import { isChangingPage } from '#app/components/utils'

type ScrollPosition = Awaited<ReturnType<RouterScrollBehavior>>

/* The scroll margin of a hash target plus the document's scroll padding, so the target lands below a sticky header */
const getHashElementScrollMarginTop = (selector: string): number => {
  try {
    const element = document.querySelector(selector)

    if (!element) {
      return 0
    }

    return (Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0)
      + (Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0)
  } catch {
    return 0
  }
}

const calculatePosition = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  savedPosition: Parameters<RouterScrollBehavior>[2]
): ScrollPosition => {
  if (savedPosition) {
    return savedPosition
  }

  if (to.hash) {
    return {
      el: to.hash,
      top: getHashElementScrollMarginTop(to.hash),
      behavior: isChangingPage(to, from) ? 'auto' : 'instant',
    }
  }

  return { left: 0, top: 0 }
}

/**
 * Nuxt's own scroll behaviour, except that it scrolls as soon as the next page has landed instead of after the page
 * transition: the pages cross-fade, so waiting would show the next page at the previous scroll position and jump
 * once the fade is over. `page:finish` is the landing; `page:loading:end`, which Nuxt itself waits for, can fire
 * while the next page is still loading.
 */
export default <RouterOptions>{
  scrollBehavior (to, from, savedPosition) {
    const nuxtApp = useNuxtApp()
    const router = useRouter()

    if (to.path.replace(/\/$/, '') === from.path.replace(/\/$/, '')) {
      if (from.hash && !to.hash) {
        return { left: 0, top: 0 }
      }

      if (to.hash) {
        return { el: to.hash, top: getHashElementScrollMarginTop(to.hash) }
      }

      return false
    }

    const routeAllowsScrollToTop = typeof to.meta.scrollToTop === 'function'
      ? to.meta.scrollToTop(to, from)
      : to.meta.scrollToTop

    if (routeAllowsScrollToTop === false) {
      return false
    }

    if (from === START_LOCATION) {
      return calculatePosition(to, from, savedPosition)
    }

    return new Promise((resolve) => {
      nuxtApp.hooks.hookOnce('page:finish', () => {
        requestAnimationFrame(() => {
          if (router.currentRoute.value.fullPath !== to.fullPath) {
            resolve(false)
            return
          }

          resolve(calculatePosition(to, from, savedPosition))
        })
      })
    })
  },
}
